import React, { useContext, useCallback, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import SignatureSettings from '../../../components/SignatureSettings';
import { ColorButton } from '../../../artboard/Toolbar/styles';
import { OPTIONS_KEY, FIELD_NAME_KEY, TEXT_DEBOUNCE_TIME_OUT } from '../../../utils/complexDocumentConstants';
import { PageElementsContext } from '../../../context/pageElementsContext';
import ColorPicker from '../../../artboard/ColorPicker';
import { ELEMENTS_SETTINGS_TOOLBAR } from '../../../artboard/Toolbar/translationValues';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { FillableElementWrapper } from '../styles';

const NAMESPACES = ['ELEMENTS_SETTINGS_TOOLBAR'];
const getElementDetails = el => el;

const SignatureEditor = props => {
  const { element, selected, editable } = props;
  const { updateComplexElementDetails } = useContext(PageElementsContext);
  const [picker, setPicker] = useState(false);
  const { t } = useTranslation(NAMESPACES);

  const [, updateArtboard] = useArtboardContext(() => null);
  useEffect(() => {
    // close the sidebar when the element is removed from the block.
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    };
  }, []);

  const debouncedUpdateComplexElementDetails = useRef(_.debounce(updateComplexElementDetails, TEXT_DEBOUNCE_TIME_OUT)).current;

  const showBackgroundColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'backgroundColor') {
        return false;
      }
      return { type: 'backgroundColor', color: element.style.backgroundColor };
    });
  }, [element]);

  const showBorderColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'borderColor') {
        return false;
      }
      return { type: 'borderColor', color: element.style.border?.borderColor };
    });
  }, [element]);

  const onColorPick = useCallback(
    color => {
      if (picker.type === 'backgroundColor') {
        updateComplexElementDetails(element.uuid, { backgroundColor: color.hex }, 'style');
        setPicker({ type: 'backgroundColor', color: color.hex });
      } else if (picker.type === 'borderColor') {
        setPicker({ type: 'borderColor', color: color.hex });
        updateComplexElementDetails(element.uuid, { border: { borderColor: color.hex } }, 'style');
      }
    },
    [picker],
  );

  const onFieldNameChange = useCallback(value => {
    debouncedUpdateComplexElementDetails(
      element.uuid,
      { options: { [FIELD_NAME_KEY]: value } },
      'form-options',
    );
    return value;
  }, []);

  return (
    <>
      <div
        style={{
          height: '100%',
          width: ' 100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {' '}
        {element.content}
      </div>
      {editable && selected && (
        <>
          {ReactDOM.createPortal(
            <SignatureSettings
              style={{ top: 10 }}
              element={element}
              onFieldNameChange={onFieldNameChange}
              FIELD_NAME_KEY={FIELD_NAME_KEY}
              OPTIONS_KEY={OPTIONS_KEY}
              getElementDetails={getElementDetails}
            />,
            document.getElementById('sidebar-absolute-container'),
          )}
          {ReactDOM.createPortal(
            <FillableElementWrapper>
              <Tooltip
                placement="bottom"
                title={t(
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.key,
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.value,
                )}
              >
                <ColorButton
                  style={{
                    backgroundColor: element.style.backgroundColor,
                    position: 'relative',
                  }}
                  onClick={showBackgroundColorPicker}
                />
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.key,
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.value,
                )}
              >
                <ColorButton
                  style={{
                    backgroundColor: element.style.border?.borderColor,
                    position: 'relative',
                  }}
                  onClick={showBorderColorPicker}
                />
              </Tooltip>
              {picker && (
                <div style={{ position: 'absolute', top: '100%' }}>
                  <ColorPicker
                    onChange={onColorPick}
                    onBlur={() => setPicker(false)}
                    color={picker.color}
                    qlEditor={true}
                  />
                </div>
              )}
            </FillableElementWrapper>,
            document.getElementById('document-editor-toolbar'),
          )}
        </>
      )}
    </>
  );
};

export default SignatureEditor;
